import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Dropdown } from 'react-bootstrap';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import CallAPI from '../../api/call';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  // State to toggle between line and bar chart
  const [showLineChart, setShowLineChart] = useState(true);
  const [calls, setCalls] = useState({});
  const [costs, setCosts] = useState({}); // State to store costs
  const [hourlyData, setHourlyData] = useState(Array(24).fill(0)); // Initialize with 24 zeros
  const [callKeys, setCallKeys] = useState([]); // State to store all keys
  const [selectedKey, setSelectedKey] = useState('total'); // State to store the selected key

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [callsData, hourlyCallsData, costsData] = await Promise.all([
          CallAPI.getCalls(),
          CallAPI.getHourlyCalls(selectedKey),
          CallAPI.getCallCosts() // Fetch costs data
        ]);
        setCalls(callsData);
        setCosts(costsData); // Set costs data
        setCallKeys(Object.keys(callsData)); // Store all keys
        processHourlyData(hourlyCallsData.call_times);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [selectedKey]); // Re-fetch data when selectedKey changes

  const processHourlyData = (callTimes) => {
    // Initialize array with 24 zeros (one for each hour)
    const processedData = Array(24).fill(0);

    // Process each call time entry
    callTimes.forEach(entry => {
      // Parse the ISO datetime string
      const date = new Date(entry.hour);
      // Get hour in 24-hour format (0-23)
      const hour = date.getHours();
      // Set the call count for that hour
      processedData[hour] = entry.call_count;
    });

    setHourlyData(processedData);
  };

  // Updated chart data using the processed hourly data
  const chartData = {
    labels: [
      '12AM', '1AM', '2AM', '3AM', '4AM', '5AM', '6AM', '7AM', '8AM', '9AM', 
      '10AM', '11AM', '12PM', '1PM', '2PM', '3PM', '4PM', '5PM', '6PM', 
      '7PM', '8PM', '9PM', '10PM', '11PM'
    ],
    datasets: [{
      label: 'Calls per Hour',
      data: hourlyData,
      backgroundColor: 'rgba(54, 162, 235, 0.5)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 2,
      tension: 0.4
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Call Progression Throughout the Day'
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Hour of the Day'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Total Calls Made'
        }
      }
    }
  };

  // Prepare data for the pie chart
  const pieChartData = {
    labels: Object.keys(costs.costs_by_outcome || {}),
    datasets: [{
      label: 'Cost Distribution',
      data: Object.values(costs.costs_by_outcome || {}).map(cost => cost || 0),
      backgroundColor: [
        'rgba(54, 162, 235, 0.5)', // Color for positive
        'rgba(255, 99, 132, 0.5)', // Color for negative
        'rgba(75, 192, 192, 0.5)', // Additional colors for other outcomes
        'rgba(153, 102, 255, 0.5)',
        'rgba(255, 159, 64, 0.5)',
      ],
      borderColor: [
        'rgba(54, 162, 235, 1)',
        'rgba(255, 99, 132, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
        'rgba(255, 159, 64, 1)',
      ],
      borderWidth: 1,
    }]
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(tooltipItem) {
            return `${tooltipItem.label}: $${tooltipItem.raw.toFixed(2)}`; // Show value on hover
          }
        }
      }
    }
  };

  // Function to capitalize each word in a string
  const capitalizeWords = (str) => {
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Function to sort the keys based on priority
  const getSortedKeys = (calls) => {
    const priorityOrder = ['total', 'positive', 'negative'];
    const remainingKeys = Object.keys(calls).filter(key => !priorityOrder.includes(key));
    return [...priorityOrder.filter(key => key in calls), ...remainingKeys];
  };

  return (
    <Container fluid className="dashboard-container p-4">
      {/* Costs Section */}
      <Row className="mb-4">
        <Col xs={12}>
          <h3>Costs</h3>
          <Row className="mb-3">
            <Col xs={12} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body className="text-center">
                  <Card.Title>Overall Total Cost</Card.Title>
                  <h2 className="mb-0">${costs.overall_total_cost?.toFixed(2) || '0.00'}</h2>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body className="text-center">
                  <Card.Title>Today's Total Cost</Card.Title>
                  <h2 className="mb-0">${costs.today_total_cost?.toFixed(2) || '0.00'}</h2>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row>
            {['positive', 'negative'].filter(key => costs.costs_by_outcome && key in costs.costs_by_outcome).map((key) => (
              <Col xs={12} md={4} className="mb-3" key={key}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <Card.Title>{`Cost for ${capitalizeWords(key)}`}</Card.Title>
                    <h2 className={`mb-0 ${key === 'positive' ? 'text-success' : 'text-danger'}`}>
                      ${costs.costs_by_outcome[key]?.toFixed(2) || '0.00'}
                    </h2>
                  </Card.Body>
                </Card>
              </Col>
            ))}
            {Object.keys(costs.costs_by_outcome || {}).filter(key => !['positive', 'negative'].includes(key)).map((key) => (
              <Col xs={12} md={4} className="mb-3" key={key}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <Card.Title>{`Cost for ${capitalizeWords(key)}`}</Card.Title>
                    <h2 className="mb-0">${costs.costs_by_outcome[key]?.toFixed(2) || '0.00'}</h2>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          {/* Conditionally render the Pie Chart Section */}
          {Object.keys(costs.costs_by_outcome || {}).length > 0 && (
            <Row className="mb-4">
              <Col xs={12}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <Card.Title className="text-center">Cost Distribution</Card.Title>
                    <div className="d-flex justify-content-center" style={{ height: '400px' }}>
                      <Pie data={pieChartData} options={pieChartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      <hr /> {/* Horizontal line break */}

      {/* Call Stats Section */}
      <Row className="mb-4">
        <h3>Today's Call Stats</h3>
        {getSortedKeys(calls).map((key) => (
          <Col xs={12} md={4} className="mb-3" key={key}>
            <Card className="h-100 shadow-sm">
              <Card.Body className="text-center">
                <Card.Title>{`${capitalizeWords(key.replace('_', ' '))} Calls`}</Card.Title>
                <h2 className={`mb-0 ${key === 'positive' ? 'text-success' : key === 'negative' ? 'text-danger' : ''}`}>
                  {calls[key]}
                </h2>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Chart Card Row */}
      <Row>
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title className="mb-0">Call Analytics</Card.Title>
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowLineChart(!showLineChart)}
                  >
                    Switch to {showLineChart ? 'Bar' : 'Line'} Chart
                  </Button>
                  <Dropdown className="ms-2">
                    <Dropdown.Toggle variant="outline-secondary" id="dropdown-basic" size="sm">
                      {capitalizeWords(selectedKey.replace('_', ' '))}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {getSortedKeys(calls).map((key) => (
                        <Dropdown.Item 
                          key={key} 
                          onClick={() => {
                            setSelectedKey(key); // Update selected key
                          }}
                        >
                          {capitalizeWords(key.replace('_', ' '))}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
              <div style={{ height: '400px' }}>
                {showLineChart ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <Bar data={chartData} options={chartOptions} />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard; 